import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { useI18n } from '../hooks/useI18n';
import {
  Menu,
  Search,
  User,
  LogOut,
  HelpCircle,
  ChevronDown,
  Home,
  BarChart3,
  FileText,
  Users,
  Shield,
  Building2,
  Check
} from 'lucide-react';
import SupportModal from './SupportModal';

interface TopNavProps {
  onSidebarToggle: () => void;
}

const TopNav: React.FC<TopNavProps> = ({ onSidebarToggle }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCompanyMenu, setShowCompanyMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSupportModal, setShowSupportModal] = useState(false);
  const { user, logout } = useAuth();
  const { selectedCompany, companies, loading, setSelectedCompany } = useCompany();
  const { t, isRTL } = useI18n();
  const location = useLocation();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const companyMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (companyMenuRef.current && !companyMenuRef.current.contains(event.target as Node)) {
        setShowCompanyMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Dashboard';
    if (path === '/admin/dashboard') return 'Admin Dashboard';
    if (path === '/profile') return 'Profile';
    if (path === '/admin/users') return 'User Management';
    if (path === '/admin/security') return 'Security';
    return 'Dashboard';
  };

  const getBreadcrumbs = () => {
    const path = location.pathname;
    const breadcrumbs = [];

    if (path.startsWith('/admin')) {
      breadcrumbs.push({ label: 'Admin', path: '/admin/dashboard' });
    }

    const pageTitle = getPageTitle();
    if (pageTitle !== 'Dashboard' && pageTitle !== 'Admin Dashboard') {
      breadcrumbs.push({ label: pageTitle, path: location.pathname });
    }

    return breadcrumbs;
  };

  const getInitials = (email: string) => {
    return email.split('@')[0].substring(0, 2).toUpperCase();
  };

  return (
    <header className={`bg-white border-b border-secondary-200 px-6 py-4 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="flex items-center justify-between">
        {/* Left Section */}
        <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-4'}`}>
          {/* Sidebar Toggle */}
          <button
            onClick={onSidebarToggle}
            className="lg:hidden p-2 rounded-lg hover:bg-secondary-100 transition-colors"
          >
            <Menu className="w-5 h-5 text-secondary-600" />
          </button>

          {/* Page Info */}
          <div className="hidden sm:block">
            <h1 className="text-xl font-semibold text-secondary-900">
              {getPageTitle()}
            </h1>
            {getBreadcrumbs().length > 0 && (
              <nav className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'} text-sm text-secondary-500 mt-1`}>
                <Link to="/dashboard" className="hover:text-primary-600 transition-colors">
                  {t('navigation.home')}
                </Link>
                {getBreadcrumbs().map((crumb, index) => (
                  <React.Fragment key={crumb.path}>
                    <span>/</span>
                    <Link
                      to={crumb.path}
                      className="hover:text-primary-600 transition-colors"
                    >
                      {crumb.label}
                    </Link>
                  </React.Fragment>
                ))}
              </nav>
            )}
          </div>
        </div>

        {/* Right Section */}
        <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-4'}`}>
          {/* Company Selector */}
          <div className="relative" ref={companyMenuRef}>
            <button
              onClick={() => setShowCompanyMenu(!showCompanyMenu)}
              className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'} px-3 py-2 border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors`}
            >
              <Building2 className="w-4 h-4 text-secondary-600" />
              <span className="text-sm font-medium text-secondary-900">
                {selectedCompany.selectedCompany ? selectedCompany.selectedCompany.name : t('topNav.selectCompany')}
              </span>
              <ChevronDown className="w-4 h-4 text-secondary-400" />
            </button>

            {/* Company Dropdown */}
            {showCompanyMenu && (
              <div className="absolute right-0 mt-2 w-80 bg-white border border-secondary-200 rounded-lg shadow-large z-50 max-h-96 overflow-y-auto">
                <div className="p-3 border-b border-secondary-200">
                  <h3 className='text-sm font-medium text-secondary-900'>{t('topNav.selectCompany')}</h3>
                </div>

                {loading ? (
                  <div className="p-4 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                    <p className='text-sm text-secondary-500 mt-2'>{t('topNav.loadingCompanies')}</p>
                  </div>
                ) : companies.length === 0 ? (
                  <div className="p-4 text-center">
                    <p className='text-sm text-secondary-500'>{t('topNav.noCompaniesAvailable')}</p>
                  </div>
                ) : (
                  <div className="p-2">
                    {companies.map((company) => (
                      <button
                        key={company.id}
                        onClick={() => {
                          setSelectedCompany(company.id);
                          setShowCompanyMenu(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${selectedCompany.selectedCompanyId === company.id
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-secondary-700 hover:bg-secondary-50'
                          }`}
                      >
                        <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'}`}>
                          <Building2 className="w-4 h-4" />
                          <div className="text-left">
                            <p className="font-medium">{company.name}</p>
                            {company.email && (
                              <p className="text-xs text-secondary-500">{company.email}</p>
                            )}
                          </div>
                        </div>
                        {selectedCompany.selectedCompanyId === company.id && (
                          <Check className="w-4 h-4 text-primary-600" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'} p-2 rounded-lg hover:bg-secondary-100 transition-colors`}
            >
              <div className="w-8 h-8 bg-gradient-primary rounded-full flex items-center justify-center text-white text-sm font-semibold">
                {getInitials(user?.email || '')}
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-secondary-900">
                  {user?.email}
                </p>
                <p className="text-xs text-secondary-500">
                  {user?.isAdmin ? t('topNav.administrator') : t('topNav.subscriber')}
                </p>
              </div>
              <ChevronDown className="w-4 h-4 text-secondary-400" />
            </button>

            {/* User Dropdown */}
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-white border border-secondary-200 rounded-lg shadow-large z-50">
                {/* User Info */}
                <div className="p-4 border-b border-secondary-200">
                  <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'}`}>
                    <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center text-white font-semibold">
                      {getInitials(user?.email || '')}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-secondary-900">
                        {user?.email}
                      </p>
                      <p className="text-xs text-secondary-500">
                        {user?.isAdmin ? t('topNav.administrator') : t('topNav.subscriber')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="p-2">
                  <Link
                    to="/profile"
                    className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'} px-3 py-2 text-sm text-secondary-700 hover:bg-secondary-50 rounded-lg transition-colors`}
                    onClick={() => setShowUserMenu(false)}
                  >
                    <User className="w-4 h-4" />
                    <span>{t('topNav.myProfile')}</span>
                  </Link>

                  <button
                    onClick={() => {
                      setShowSupportModal(true);
                      setShowUserMenu(false);
                    }}
                    className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'} px-3 py-2 text-sm text-secondary-700 hover:bg-secondary-50 rounded-lg transition-colors w-full`}
                  >
                    <HelpCircle className="w-4 h-4" />
                    <span>{t('topNav.helpSupport')}</span>
                  </button>

                  <div className="border-t border-secondary-200 my-2"></div>

                  <button
                    onClick={handleLogout}
                    className={`w-full flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'} px-3 py-2 text-sm text-error-600 hover:bg-error-50 rounded-lg transition-colors`}
                  >
                    <LogOut className="w-4 h-4" />
                    <span>{t('topNav.signOut')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Support Modal */}
      <SupportModal isOpen={showSupportModal} onClose={() => setShowSupportModal(false)} />
    </header>
  );
};

export default TopNav;
